import { AlertTriangle } from 'lucide-react';

/**
 * ConflictModal — Q32 Phase B.
 *
 * Shown before a user joins a plan that overlaps with another joined or
 * hosted plan on the same date. Users can choose to continue anyway or
 * cancel — the modal does NOT call the join API itself; it just confirms.
 *
 * Props:
 *   open      — bool
 *   conflicts — [{ id, title, start_time, end_time, date, is_host }]
 *   onContinue — () => void   (proceed with join)
 *   onCancel   — () => void   (close + abort)
 */
export default function ConflictModal({ open, conflicts, onContinue, onCancel }) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[10002] flex items-center justify-center px-6 bg-black/50 backdrop-blur-[2px] animate-fade-in"
      onClick={onCancel}
      data-testid="conflict-modal-backdrop"
    >
      <div
        className="w-full max-w-[360px] bg-white rounded-3xl shadow-2xl p-6 animate-scale-in"
        onClick={e => e.stopPropagation()}
        data-testid="conflict-modal"
      >
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-full bg-[#FFC107]/20 text-[#B57F00] flex items-center justify-center">
            <AlertTriangle size={18} />
          </div>
          <h3 className="text-base font-extrabold text-[#111]">This plan overlaps with:</h3>
        </div>

        <ul data-testid="conflict-list" className="space-y-2 mb-5">
          {conflicts.map(c => (
            <li
              key={c.id}
              data-testid={`conflict-item-${c.id}`}
              className="bg-[#FAFAF7] rounded-xl p-3 border border-black/[0.04]"
            >
              <p className="text-sm font-extrabold text-[#111] truncate">&ldquo;{c.title}&rdquo;</p>
              <p className="text-[11px] text-[#6B6E73] mt-0.5">
                {formatTimeRange(c.start_time, c.end_time)} {c.is_host && '· hosting'}
              </p>
            </li>
          ))}
        </ul>

        <div className="flex flex-col gap-2">
          <button
            data-testid="conflict-continue-btn"
            onClick={onContinue}
            className="w-full bg-[#B6E600] text-[#111] font-bold rounded-full text-sm active:scale-[0.98]"
            style={{ minHeight: 50 }}
          >
            Continue anyway
          </button>
          <button
            data-testid="conflict-cancel-btn"
            onClick={onCancel}
            className="w-full bg-transparent text-[#111] font-semibold rounded-full text-sm active:scale-[0.98]"
            style={{ minHeight: 44 }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function formatTimeRange(start, end) {
  const s = format12(start);
  const e = format12(end);
  if (s && e) return `${s} – ${e}`;
  if (s) return s;
  return 'time unknown';
}

function format12(hhmm) {
  if (!hhmm || typeof hhmm !== 'string') return '';
  const [h, m] = hhmm.split(':');
  const hr = parseInt(h, 10);
  if (Number.isNaN(hr)) return '';
  const period = hr >= 12 ? 'PM' : 'AM';
  const display = ((hr + 11) % 12) + 1;
  return `${display}:${(m || '00').slice(0, 2)} ${period}`;
}
